import { SsoRedirectError } from "./errors";
import {
  buildSsoRedirectUrl,
  DEFAULT_SSO_START_URL,
  isLocalDevHost,
} from "./sso";

export type AuthFetchEnv = {
  /** Current page URL used as `rd` after SSO. Default: `location.href`. */
  getLocationHref?: () => string;
  /** Hostname used for the localhost SSO skip. Default: `location.hostname`. */
  getHostname?: () => string;
  /** Navigation sink. Default: assign `location.href`. */
  assignLocation?: (url: string) => void;
  /** Injected fetch (tests / non-browser). Default: global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Override SSO start base. Default: production oauth2-proxy URL. */
  ssoStartUrl?: string;
  /**
   * When true (default), a 401 on localhost/127.0.0.1 is returned to the
   * caller instead of redirecting — same behavior as pod-v0.html:1680.
   */
  skipSsoOnLocalhost?: boolean;
  /**
   * When false, a 401 is returned to the caller without navigating to SSO.
   * Used by SessionProvider's probe so AuthGate owns redirect UX.
   * Default true (production fetch-wrapper behavior from AAA-001 §5).
   */
  redirectOn401?: boolean;
};

export type AuthFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

function defaultAssignLocation(url: string): void {
  if (typeof globalThis.location !== "undefined") {
    (globalThis.location as Location).href = url;
    return;
  }
  throw new Error(
    `auth-client: cannot redirect to ${url} — no location.assign available`,
  );
}

function defaultHostname(): string {
  if (typeof globalThis.location !== "undefined") {
    return globalThis.location.hostname;
  }
  return "";
}

function defaultHref(): string {
  if (typeof globalThis.location !== "undefined") {
    return globalThis.location.href;
  }
  return "";
}

/**
 * credentials:'include' fetch wrapper + 401 → SSO redirect.
 *
 * Generalizes the three AAA-001 §5 duplicates into one Platform package.
 * Always forces `credentials: 'include'` (callers cannot opt out — that is
 * the auth-bootstrap contract).
 */
export function createAuthFetch(env: AuthFetchEnv = {}): AuthFetch {
  const fetchImpl = env.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const skipLocal = env.skipSsoOnLocalhost !== false;
  const redirectOn401 = env.redirectOn401 !== false;
  const ssoStartUrl = env.ssoStartUrl ?? DEFAULT_SSO_START_URL;

  return async function authFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const merged: RequestInit = { ...(init ?? {}), credentials: "include" };
    const response = await fetchImpl(input, merged);

    if (response.status !== 401) {
      return response;
    }

    if (!redirectOn401) {
      return response;
    }

    const hostname = (env.getHostname ?? defaultHostname)();
    if (skipLocal && isLocalDevHost(hostname)) {
      return response;
    }

    const returnUrl = (env.getLocationHref ?? defaultHref)();
    const redirectTo = buildSsoRedirectUrl(returnUrl, ssoStartUrl);
    (env.assignLocation ?? defaultAssignLocation)(redirectTo);
    throw new SsoRedirectError();
  };
}

/** Default singleton for simple call sites (browser). */
export const authFetch: AuthFetch = createAuthFetch();
